'use client';

import { useState, useRef } from 'react';
import clsx from 'clsx';
import Slider from 'components/commercetools-ui/atoms/slider';
import Link from 'components/commercetools-ui/atoms/link';
import Image from 'next/image';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { setFFIDsForPagination } from '../curated-slider/helpers';
import { smallMobile, mobile, tablet, desktop, mediumDesktop, largeDesktop, hugeDesktop } from 'helpers/utils/screensizes';
import { getIcon } from 'components/icons/';
import { trackCtaClick} from 'helpers/analytics/ga4Events';
import { useTranslations } from 'use-intl'
import { useSliderAccessibility } from 'helpers/hooks/useSliderAccessibility';

interface MerchandisingRailProps {
  headerText?: string,
  headerFontSize?: number,
  headerFontSizeMobile?: number,
  headerFontColor?: string,
  subHeaderText?: string,
  subHeaderFontSize?: number,
  subHeaderFontSizeMobile?: number,
  subHeaderFontColor?: string,
  tiles: [MerchandisingRailTileProps],
};

interface MerchandisingRailTileProps {
  category?: string,
  icon?: unknown,
  tileText: string,
  tileFontSize?: number,
  tileFontSizeMobile?: number
  imageSrc: string,
  imageAlt: string,
  ctaLink: any,
  ctaText: string,
  backgroundColor?: string,
  tileTextColor?: string,
};

interface TileProps {
  data: MerchandisingRailTileProps,
  ffidSuffix: string,
  isPureMobile: boolean,
};

const MOBILE_SLIDE_GAP = 12;
const DESKTOP_SLIDE_GAP = 24;

function Tile({ data, ffidSuffix, isPureMobile }: TileProps) {
  const ffidConstant = 'merchandising-rail-tile'
  const categoryHeadingHeight = isPureMobile ? 16 : 24;
  const Icon = getIcon(data.icon);

  return (
    <div
      className={`flex hover-lift-parent lift-target ${isPureMobile ? 'rounded-[12px] w-[362px]': 'rounded-[20px] w-[552px]'}`}
      data-ffid={`ctr-${ffidConstant}-${ffidSuffix}`}
    >
      <div
        style={{
          backgroundColor: data.backgroundColor,
          color: data.tileTextColor || '#FFF',
          borderTopLeftRadius: 'inherit',
          borderBottomLeftRadius: 'inherit',
          lineHeight: '1.4'
        }}
        className={`grow flex flex-col justify-between ${isPureMobile ? 'p-24' : 'py-55 px-40'}`}
      >
        <div>
          <div className={`flex items-center justify-center w-full ${isPureMobile ? 'gap-4 mb-8' : 'gap-8 mb-10'}`}>
            {Icon && (
              <span aria-hidden="true">
                <Icon size={categoryHeadingHeight} />
              </span>
            )}
            <span
              className="leading-none"
              style={{ transform: 'translateY(1px)',
                fontSize: isPureMobile
                ? '14px'
                : '16px',
              }}
              data-ffid={`lbl-${ffidConstant}-${ffidSuffix}`}
            >
              {data.category}
            </span>
          </div>
          <h3
            className="block text-center"
            style={{
              fontSize: isPureMobile
                ? `${data.tileFontSizeMobile || '20'}px`
                : `${data.tileFontSize || '28'}px`,
            }}
          >
            {data.tileText}
          </h3>
        </div>
        <Link
          style={{ backgroundColor: 'white', color: '#000' }}
          className={`block rounded-2xl text-center ${isPureMobile ? 'text-14 py-8 px-32' : 'text-18 py-10 px-48'}`}
          link={data.ctaLink}
          data-ffid={`lnk-${ffidConstant}-${ffidSuffix}`}
          onClick={() => {
            trackCtaClick('merchandising_rail', data.category || data.tileText, data.ctaLink.link || data.ctaLink.pageFolder._url);
          }}
        >
          {data.ctaText}
        </Link>
      </div>
      <Image
        src={data.imageSrc}
        alt={data.imageAlt}
        data-ffid={`img-${ffidConstant}-${ffidSuffix}`}
        style={{ borderTopRightRadius: 'inherit', borderBottomRightRadius: 'inherit' }}
        unoptimized={true}
        priority={true}
        height={isPureMobile ? 191 : 306}
        width={isPureMobile ? 191 : 306}
      />
    </div>
  );
}

export default function MerchandisingRail({
    headerText,
    headerFontSize,
    headerFontSizeMobile,
    headerFontColor,
    subHeaderText,
    subHeaderFontSize,
    subHeaderFontSizeMobile,
    subHeaderFontColor,
    tiles,
  }: MerchandisingRailProps) {

  // note, the 1st value is explicitly skipped so the comma is necessary
  const [, width] = useMediaQuery(mobile);
  const isPureMobile = width < tablet;
  const [, setCurrentSlidesPerView] = useState(0);
  const layoutAnchorRef = useRef<HTMLDivElement>(null);
  const ffidConstant = 'merchandising-rail';
  const translate = useTranslations();
  const prevTranslation = translate('common.prev');
  const nextTranslation = translate('common.next');

  const getVisibleCount = () => {
    return width < mediumDesktop ? 1 : 2;
  }

  const {
    handleSliderContainerFocus,
    handleSliderContainerBlur,
    handleSlideChange,
    isSliderFocused,
    slideDescription,
    slideAnnouncement
  } = useSliderAccessibility(tiles.map((tile: MerchandisingRailTileProps) => tile.category || tile.tileText), getVisibleCount);

  return (
    <div
      className={`py-20 ${isPureMobile && 'ml-20'}`}
      ref={layoutAnchorRef}
      data-ffid={`ctr-${ffidConstant}-slider`}
    >
      <div className="curated-slider product-recommend container relative">
        <div className={`flex flex-col items-center ${!isPureMobile && 'mb-18'}`}>
          <h2
            className="w-full text-center leading-[1.2]"
            style={{
              color: headerFontColor,
              fontSize: `${isPureMobile ? headerFontSizeMobile : headerFontSize}px`,
            }}
            data-ffid={`lbl-${ffidConstant}-heading`}
          >
            {headerText}
          </h2>
          <p
            className="w-full leading-[1.4] text-center font-light"
            style={{
              color: subHeaderFontColor,
              fontSize: `${isPureMobile ? subHeaderFontSizeMobile : subHeaderFontSize}px`,
            }}
            data-ffid={`lbl-${ffidConstant}-secondary-heading`}
          >
            {subHeaderText}
          </p>
        </div>
        <section
          aria-label="Merchandising Rail"
          aria-roledescription="carousel"
          data-ffid={`fra-${ffidConstant}`}
          onFocus={handleSliderContainerFocus}
          onBlur={handleSliderContainerBlur}
        >
          <Slider
            layoutRef={layoutAnchorRef}
            a11y={{
              enabled: true,
              prevSlideMessage: prevTranslation,
              nextSlideMessage: nextTranslation,
              containerRoleDescriptionMessage: '',
              slideRole: 'presentation',
              itemRoleDescriptionMessage: '',
            }}
            pagination={{
              enabled: true,
              clickable: true, // makes dots clickable
              dynamicBullets: true, // larger active + neighbor bullets
            }}
            onInit={(swiper) => {
              setFFIDsForPagination(swiper, ffidConstant);
            }}
            onBreakpoint={(sw) => setCurrentSlidesPerView(sw.params.slidesPerView as number)}
            slidesPerView={'auto'}
            slideWidthIsFlexible={true}
            onSlideChange={handleSlideChange}
            aria-label={`${headerText?.trim() || ''}`}
            uniqueFFIDText={ffidConstant}
            dots={true}
            arrows={true}
            allowTouchMove={true}
            snapToSlideEdge={true}
            spaceBetween={MOBILE_SLIDE_GAP}
            ariaLabelOfPreviousButton={prevTranslation}
            ariaLabelOfNextButton={nextTranslation}
            arrowsClassName={clsx('z-20')}
            breakpoints={{
              [smallMobile]: {
                spaceBetween: MOBILE_SLIDE_GAP,
              },
              [mobile]: {
                spaceBetween: MOBILE_SLIDE_GAP,
              },
              [tablet]: {
                spaceBetween: DESKTOP_SLIDE_GAP,
              },
              [desktop]: {
                spaceBetween: DESKTOP_SLIDE_GAP,
              },
              [mediumDesktop]: {
                spaceBetween: DESKTOP_SLIDE_GAP,
              },
              [largeDesktop]: {
                spaceBetween: DESKTOP_SLIDE_GAP,
              },
              [hugeDesktop]: {
                spaceBetween: DESKTOP_SLIDE_GAP,
              },
            }}
            slideDescription={isSliderFocused ? slideDescription : undefined}
            navPosition={'right'}
          >
            {
              tiles.map((tile: any, index: any) => {
                return (
                  <Tile
                    key={`${tile.category}-${index}`}
                    data={tile}
                    ffidSuffix={index}
                    isPureMobile={isPureMobile}
                  />
                );
              })
            }
          </Slider>
          <div
            className="sr-only"
            aria-live="polite"
            aria-atomic="true"
          >
            {slideAnnouncement}
          </div>
        </section>
      </div>
    </div>
  );
}